import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { TravelPremiumRisk } from '@app/domain/model/eshop/travel/TravelPremiumRisk';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { ensureNonZeroNumber } from '@app/lib/util/ensureNonZeroNumber';
import { TravelInsuranceRequestResult } from '@app/service/eshop/travel/actions/requestTravelInsurance';
import { differenceInCalendarDays, format } from 'date-fns';
import { useTranslations } from 'next-intl';
import React from 'react';

interface Props {
    selectedRisks: TravelPremiumRisk[];
    editPolicy: () => void;
    requestResult: TravelInsuranceRequestResult;
}

export function PolicyPanel({ selectedRisks, editPolicy, requestResult }: Props) {
    const t = useTranslations();

    const periodDays = differenceInCalendarDays(requestResult.endDate, requestResult.beginDate) + 1;
    return (
        <CollapsablePanel
            addon={<TextButton onClick={editPolicy}>{t('general.edit')}</TextButton>}
            collapsable={false}
            label={t('eshop.travel.summaryStep.allInsurances')}>
            <div className="grid w-full grid-cols-3 gap-4 border-b border-neutral-150 p-6">
                {selectedRisks.map(risk => (
                    <React.Fragment key={risk.code}>
                        <ReadOnlyField className="col-span-2" label={t('eshop.travel.summaryStep.risk')}>
                            {risk.name}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.travel.summaryStep.price')}>
                            {risk.premiumAfterDiscount && ensureNonZeroNumber(risk.premiumAfterDiscount, false) ? (
                                <div className="flex flex-row gap-1">
                                    <Paragraph className="line-through" size="small">
                                        {formatEuroValue(risk.premium ?? 0, true)}
                                    </Paragraph>
                                    <Paragraph isBold>{formatEuroValue(risk.premiumAfterDiscount, true)}</Paragraph>
                                </div>
                            ) : (
                                <Paragraph isBold>{formatEuroValue(risk.premium ?? 0, true)}</Paragraph>
                            )}
                        </ReadOnlyField>
                    </React.Fragment>
                ))}
            </div>
            <div className="flex w-full flex-col gap-4 px-6 pt-6">
                <Paragraph size="medium" isBold>
                    {t('eshop.travel.summaryStep.insurancePeriod')}
                </Paragraph>

                <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3">
                    <ReadOnlyField label={t('eshop.travel.summaryStep.period')}>
                        {periodDays === 1 ? t('general.oneDay') : t.rich('general.daysCount', { count: periodDays })}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.travel.summaryStep.startDate')}>
                        {format(requestResult.beginDate, 'yyyy-MM-dd')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.travel.summaryStep.endDate')}>
                        {format(requestResult.endDate, 'yyyy-MM-dd')}
                    </ReadOnlyField>
                </div>
            </div>
        </CollapsablePanel>
    );
}
